import Model, { attr, belongsTo } from '@warp-drive/legacy/model';
import type UserModel from './user.ts';
import { Type } from '@warp-drive/core-types/symbols';

type ValueOfConst<T> = T[keyof T];

export const ACCOUNT_DECLINED_REASON = {
  doNotHave: 'do_not_have',
  doNotWant: 'do_not_want',
  other: 'other',
} as const;

export default class AnswerProfileModel extends Model {
  @attr declare providingApp: string;
  @attr declare academicDegree: string;
  @attr declare affordPayments: string;
  @attr declare currentForgivenessProgram: string;
  @attr declare currentWorkingOnForgiveness: string;
  @attr declare employmentType: string;
  @attr declare federalLoanTime: string;
  @attr declare householdIncome: number;
  @attr declare householdSize: number;
  @attr declare madeLimitedWaiverPayments: number;
  @attr declare madePayments: number;
  @attr declare payoffGoal: string;
  @attr declare planAsTeacher: string;
  @attr declare planInGovernment: string;
  @attr declare residenceState: string;
  @attr declare numberOfAdults: number;
  @attr declare numberOfChildren: number;
  @attr declare federalFirstLoanTime: string;
  @attr declare federalLastLoanTime: string;
  @attr declare teacherLoanForgiveness: string;

  @attr declare recertifiedIncomeAt: string;
  @attr declare consolidatedLoansAt: string;
  @attr declare changedRepaymentPlanAt: string;

  @attr declare accountDeclinedReason: ValueOfConst<typeof ACCOUNT_DECLINED_REASON>;
  @attr declare accountDeclinedComment: string;

  /*************************
   **  Relationships      **
   *************************/

  @belongsTo('user', { async: false, inverse: 'answerProfile' })
  declare user: UserModel;

  /**************************
   **  Computed Properties **
   **************************/

  get hasExpiredRecertification() {
    if (!this.recertifiedIncomeAt) {
      return false;
    }

    const approxTenMonthsInMilliseconds = 31556952000 * (5 / 6);
    const todayInMilliseconds = new Date().getTime();
    const recertifiedAtInMilliseconds = new Date(this.recertifiedIncomeAt).getTime();
    const elapsedMilliseconds = todayInMilliseconds - recertifiedAtInMilliseconds;
    return elapsedMilliseconds >= approxTenMonthsInMilliseconds;
  }

  [Type] = 'answer-profile' as const;
}
